import { ChevronsUpDown, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { BrandMark } from "../brand";
import { TeamCrest } from "../primitives";
import { canManageTeam, isFounderRole, teamRoleLabel } from "../../lib/labels";
import type { Team } from "../../types";
import type { View } from "./nav-config";
import { navItems } from "./nav-config";
import { RosterNavItem } from "./RosterNavItem";
import { SettingsNavItem } from "./SettingsNavItem";
import { SeasonSwitcher } from "./SeasonSwitcher";

export function DesktopSidebar({
  team,
  activeView,
  onSelect,
  userEmail,
  role,
  isAppAdmin,
  onSignOut,
  teams,
  onSwitchTeam,
  pendingRequestsCount,
  rosterBadge = 0,
}: {
  team: Team;
  activeView: View;
  onSelect: (view: View) => void;
  userEmail: string;
  role: string;
  isAppAdmin: boolean;
  onSignOut: () => void;
  teams: Team[];
  onSwitchTeam: (teamId: string) => void;
  pendingRequestsCount: number;
  rosterBadge?: number;
}) {
  const { t } = useTranslation("shell");
  const canManageMembers = isFounderRole(role) || isAppAdmin;
  const canSeeRequests = canManageTeam(role) || isAppAdmin;
  const canSwitch = teams.length > 1;
  const item = (entry: (typeof navItems)[number], badge = 0) => {
    const Icon = entry.icon;
    const isActive = activeView === entry.id;
    return (
      <button
        key={entry.id}
        onClick={() => onSelect(entry.id)}
        aria-current={isActive ? "page" : undefined}
        className={cn("w-full flex items-center gap-3 h-10 px-3 rounded-xl text-left transition-colors", isActive ? "nav-rail-pill font-semibold" : "text-muted-foreground hover:bg-raised hover:text-foreground")}
      >
        <Icon className="h-[18px] w-[18px]" />
        <span className="flex-1">{t(entry.labelKey)}</span>
        {badge > 0 && <span className="eb-data rounded-full bg-[hsl(var(--warn))] text-background px-2 text-xs tracking-normal">{badge}</span>}
      </button>
    );
  };
  const clubItems = navItems.filter((it) => it.section === "club" && (it.id !== "requests" || canSeeRequests));
  return (
    <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r sticky top-0 h-screen p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={!canSwitch}>
          <button className="w-full flex items-center gap-3 p-2 rounded-xl text-left enabled:hover:bg-raised transition-colors">
            <TeamCrest team={team} size={30} />
            <span className="min-w-0 flex-1 font-semibold leading-tight truncate" title={team.name}>{team.name}</span>
            {canSwitch && <ChevronsUpDown className="h-4 w-4 text-[hsl(var(--faint))]" />}
          </button>
        </DropdownMenuTrigger>
        {canSwitch && (
          <DropdownMenuContent align="start" className="w-56">
            {teams.map((other) => (
              <DropdownMenuItem key={other.id} onClick={() => onSwitchTeam(other.id)} className={cn("gap-2", other.id === team.id && "font-semibold")}>
                <TeamCrest team={other} size={18} />
                <span className="truncate">{other.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      <div className="px-1 pb-3"><SeasonSwitcher onManage={() => onSelect("seasons")} side="bottom" /></div>
      <div className="flex-1 overflow-y-auto space-y-5">
        <nav className="flex flex-col gap-0.5">
          {item(navItems.find((it) => it.id === "home")!)}
          {item(navItems.find((it) => it.id === "sessions")!)}
          <RosterNavItem activeView={activeView} onSelect={onSelect} badge={rosterBadge} />
          {item(navItems.find((it) => it.id === "stats")!)}
        </nav>
        <div>
          <p className="px-3 pb-1.5 text-sm text-[hsl(var(--faint))]">{t("nav.club")}</p>
          <nav className="flex flex-col gap-0.5">
            {clubItems.map((entry) => item(entry, entry.id === "requests" ? pendingRequestsCount : 0))}
            <SettingsNavItem activeView={activeView} onSelect={onSelect} canManageMembers={canManageMembers} />
            {isAppAdmin && item(navItems.find((it) => it.id === "admin")!)}
          </nav>
        </div>
      </div>
      <div className="pt-3 space-y-3">
        <div className="px-3"><BrandMark size="md" /></div>
        <div className="flex items-center gap-2.5 p-1.5">
          <div className="h-9 w-9 rounded-full bg-raised grid place-items-center text-xs font-semibold shrink-0">
            {(userEmail || "EB").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate leading-tight" title={userEmail}>{userEmail.split("@")[0] || "User"}</p>
            <p className="text-[13px] text-[hsl(var(--faint))] leading-tight">{teamRoleLabel(role) || role}</p>
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={onSignOut} title={t("common:common.logout")} aria-label={t("common:common.logout")}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
